/**
 * @fileoverview DashboardPage — Cinematic redesigned dashboard
 *
 * Hero + Live Showdown card + stat cards + My Stable widget +
 * Awards + Stats + Recent Activity + Notifications.
 *
 * winning.png lives in /public so referenced as '/winning.png' (no import).
 * All colours use CSS variables from theme.css (responds to Track Mode).
 * Existing Firestore data bindings, auth, and routing preserved.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
  collection, query, where, orderBy, onSnapshot,
  limit, doc, getDocs, updateDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { auth, db, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import ShareInviteCard from '../components/share/ShareInviteCard';
import UpcomingScheduleSection from '../components/dashboard/UpcomingScheduleSection';
import { getProfileImageUrl, DEFAULT_PROFILE_IMAGE } from '../utils/profileImage';
import { useNotificationFeed } from '../hooks/useNotificationFeed';
import { isExpired, formatRelativeTime } from '../utils/notificationHelpers';
import './DashboardPage.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

function toMs(val) {
  if (!val) return null;
  if (typeof val === 'number') return val;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (val._seconds !== undefined) return val._seconds * 1000;
  const ms = new Date(val).getTime();
  return isNaN(ms) ? null : ms;
}

function computeSlateStatus(slate) {
  if (!slate) return 'closed';
  if (slate.finalised) return 'finalised';
  const now    = Date.now();
  const openMs = toMs(slate.gameOpenTime);
  const closeMs = toMs(slate.gameCloseTime);
  if (closeMs && now >= closeMs) return 'closed';
  if (openMs  && now >= openMs)  return 'live';
  return 'opening_soon';
}

function fmt$(n) {
  if (!n && n !== 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-AU');
}

// "2026-06-29_scone_showdown" → "Scone Showdown"
function humaniseSlateId(slateId) {
  if (!slateId) return 'Unknown Showdown';
  return slateId
    .split('_')
    .slice(1)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ') || slateId;
}

// ── Countdown hook ─────────────────────────────────────────────────────────────

function useCountdown(targetMs) {
  const [diff, setDiff] = useState(targetMs ? targetMs - Date.now() : null);
  useEffect(() => {
    if (!targetMs) return;
    setDiff(targetMs - Date.now());
    const id = setInterval(() => setDiff(targetMs - Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  if (!diff || diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// ── Placeholder data (replaced by real Firestore when features land) ───────────

const PLACEHOLDER_ACTIVITY = [
  { id: 1, icon: '🐎', text: 'You entered Scone Showdown',           time: '10m ago' },
  { id: 2, icon: '🐎', text: 'You finished 8th in Narromine Test',   time: '2d ago'  },
  { id: 3, icon: '🏆', text: 'You earned the Top 10 Finisher award', time: '7d ago'  },
];

const PLACEHOLDER_MARKET = [
  { id: 1, name: 'Just A Star',  change: '+23%', sale: 'Inglis Digital', hot: true  },
  { id: 2, name: 'Quick Impact', change: '+18%', sale: 'Ready To Run',   hot: false },
  { id: 3, name: 'Royal Deel',   change: '+15%', sale: 'Magic Millions', hot: false },
];

// ── Main component ─────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();

  const [user,           setUser]           = useState(null);
  const [userDoc,        setUserDoc]        = useState(null);
  const [slates,         setSlates]         = useState([]);
  const [allSlates,      setAllSlates]      = useState([]);
  const [stables,        setStables]        = useState([]);
  const [notifications,       setNotifications]       = useState([]);
  const [friendRequests,      setFriendRequests]      = useState([]);

  // ── Auth ───────────────────────────────────────────────────────────────────
  useEffect(() => onAuthStateChanged(auth, setUser), []);

  // ── User document ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid), snap => {
      if (snap.exists()) setUserDoc(snap.data());
    });
  }, [user]);

  // ── Phase 7 backfill — claim a public profile slug for existing players
  // who already set a Stable Name before /stable/:slug shipped. Runs at
  // most once per mount (guarded by the missing-slug condition itself,
  // since a successful claim updates userDoc.stableSlug via the listener
  // above and this effect won't fire again).
  useEffect(() => {
    if (!userDoc?.username || userDoc?.stableSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const claim = httpsCallable(functions, 'claimStableSlug');
        await claim({ stableName: userDoc.username });
      } catch (e) {
        if (!cancelled) console.error('[DashboardPage] Backfill claimStableSlug error:', e.code, e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [userDoc?.username, userDoc?.stableSlug]);

  // ── Active slates — simple query to avoid composite index requirement ────
  useEffect(() => {
    const q = query(
      collection(db, 'game_slates'),
      orderBy('meetingDate', 'desc'),
      limit(20)
    );
    return onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSlates(all.filter(s => s.finalised !== true));
    });
  }, []);

  // ── Player entries (stable) ────────────────────────────────────────────────
  // Capped at the 10 most recent — everything this feeds (scratching alert
  // derivation, the live/upcoming-slate entry match, the recent-stables
  // widget) is inherently about RECENT activity, so this stays capped.
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'player_entries'),
      where('userId', '==', user.uid),
      orderBy('submittedAt', 'desc'),
      limit(10)
    );
    return onSnapshot(q, snap =>
      setStables(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [user]);

  // ── All-time totals for the "Games Played" / "Virtual Earnings" stat cards
  // ────────────────────────────────────────────────────────────────────────
  // QA fix 2026-07-06 (Phase 7 stat accuracy round 2): these two cards are
  // explicitly labelled "All Time", but were previously derived from the
  // `stables` state above — which is capped to the 10 MOST RECENT entries.
  // For a player with more than 10 games that silently underrepresented
  // both games played and total earnings (or, depending on which games
  // fell in/out of the most-recent-10 window, could differ arbitrarily
  // from the true all-time total, which is what caused the Dashboard
  // ($7,352,880 from only 10 entries) vs My Stable/public profile
  // ($8,975,910 from all 17) mismatch). A dedicated unlimited query keeps
  // the `stables` cap above intact for its recency-based uses while making
  // these two cards genuinely all-time. financialAudit.totalPrizeDividendsWon
  // is the same canonical field My Stable's getEntryEarnings() and the
  // public stable profile's syncPublicProfileStats() both use — never a
  // second/independent earnings calculation (see utils/stableStatus.js).
  const [allTimeTotals, setAllTimeTotals] = useState({ gamesPlayed: 0, totalEarnings: 0 });
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'player_entries'), where('userId', '==', user.uid));
    return onSnapshot(q, snap => {
      let earnings = 0;
      snap.docs.forEach(d => {
        earnings += d.data().financialAudit?.totalPrizeDividendsWon ?? 0;
      });
      setAllTimeTotals({ gamesPlayed: snap.size, totalEarnings: earnings });
    });
  }, [user]);

  // ── Phase 8 — Recent Achievements ────────────────────────────────────────
  // player_profiles/{uid}.badges is the FULL list of real, server-verified
  // earned achievements (functions/src/achievements/achievementEngine.js
  // updateProfileBadgeSummary) — sorted tier-first for the trophy cabinet.
  // Here we re-sort by earnedAt (newest first) for a genuinely "recent"
  // widget, then take the top 3. Never a client-side achievement guess —
  // this is the exact same publicly-readable doc /stable/:slug renders.
  const [recentBadges, setRecentBadges] = useState([]);
  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'player_profiles', user.uid), snap => {
      const badges = snap.exists() ? (snap.data().badges || []) : [];
      const sorted = [...badges].sort((a, b) => (toMs(b.earnedAt) || 0) - (toMs(a.earnedAt) || 0));
      setRecentBadges(sorted.slice(0, 3));
    });
  }, [user]);

  // ── Keep allSlates in sync with the live slates subscription ─────────────
  useEffect(() => {
    setAllSlates(prev => {
      const existingIds = new Set(prev.map(s => s.id));
      const merged = [...prev];
      slates.forEach(s => {
        if (!existingIds.has(s.id)) merged.push(s);
        else {
          const idx = merged.findIndex(x => x.id === s.id);
          if (idx !== -1) merged[idx] = s;
        }
      });
      return merged;
    });
  }, [slates]);

  // ── Fetch completed slates that player has entries in but aren't loaded ───
  useEffect(() => {
    if (stables.length === 0) return;
    const loadedIds = new Set(allSlates.map(s => s.id));
    const missing = stables
      .map(e => e.slateId)
      .filter(Boolean)
      .filter(id => !loadedIds.has(id));
    if (missing.length === 0) return;

    const chunks = [];
    for (let i = 0; i < missing.length; i += 10) {
      chunks.push(missing.slice(i, i + 10));
    }
    Promise.all(
      chunks.map(chunk =>
        getDocs(query(
          collection(db, 'game_slates'),
          where('__name__', 'in', chunk)
        ))
      )
    ).then(results => {
      const extra = results.flatMap(r =>
        r.docs.map(d => ({ id: d.id, ...d.data() }))
      );
      setAllSlates(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        return [...prev, ...extra.filter(s => !existingIds.has(s.id))];
      });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stables]);

  // ── Derive notifications from scratch alerts ───────────────────────────────
  useEffect(() => {
    const alerts = stables.flatMap(e =>
      (e.scratchingAlerts || [])
        .filter(a => !a.dismissed)
        .map(a => ({ ...a, entryId: e.id, slateId: e.slateId }))
    );
    setNotifications(alerts);
  }, [stables]);

  // ── Pending friend requests ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'friend_requests'),
      where('toUserId', '==', user.uid),
      where('status', '==', 'pending')
    );
    return onSnapshot(q, snap =>
      setFriendRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [user]);

  // ── System notifications (broadcast + welcome) ────────────────────────────
  // QA fix 2026-07-06 (Phase 7 stat accuracy round 2, item 7/10): shares the
  // SAME notifications/{uid}/items listener NotificationCenter/ToastHost use
  // (useNotificationFeed) instead of a bespoke read==false query, so the
  // Dashboard panel, bell, and center can never disagree about what counts
  // as "dismissed" or "expired". Shows unread AND read-but-not-dismissed —
  // only `status === 'dismissed'` or an expired `expiresAt` hides a row.
  const { items: notificationFeedItems } = useNotificationFeed(user, { limit: 10 });
  const systemNotifications = notificationFeedItems.filter(
    n => n.status !== 'dismissed' && !isExpired(n)
  );

  async function dismissSystemNotif(notifId) {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'notifications', user.uid, 'items', notifId), {
        status: 'dismissed', dismissedAt: serverTimestamp(), read: true,
      });
    } catch (e) {
      console.error('[Dashboard] dismissSystemNotif error:', e.message);
    }
  }

  async function dismissAllSystemNotifs() {
    if (!user || systemNotifications.length === 0) return;
    try {
      const batch = writeBatch(db);
      systemNotifications.forEach(n => {
        batch.update(doc(db, 'notifications', user.uid, 'items', n.id), {
          status: 'dismissed', dismissedAt: serverTimestamp(), read: true,
        });
      });
      await batch.commit();
    } catch (e) {
      console.error('[Dashboard] dismissAllSystemNotifs error:', e.message);
    }
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const displayName    = userDoc?.displayName || user?.displayName || '';
  const firstName      = displayName.split(' ')[0] || '';
  const greetingText   = firstName ? `Hello ${firstName}` : 'Hello Stable Owner';
  const profileImageUrl = getProfileImageUrl(userDoc, user);

  // Priority: entered live > any live > entered upcoming > any upcoming
  const { liveSlate, nextSlate, liveEntry } = useMemo(() => {
    const enteredIds = new Set(stables.map(e => e.slateId).filter(Boolean));
    const nonFinal   = slates.filter(s => !s.finalised);
    const live       = nonFinal.filter(s => computeSlateStatus(s) === 'live');
    const soon       = nonFinal.filter(s => computeSlateStatus(s) === 'opening_soon');
    const active     = live.find(s => enteredIds.has(s.id)) || live[0] || null;
    const next       = soon.find(s => s.id !== active?.id) || null;
    const entry      = active ? stables.find(e => e.slateId === active.id) || null : null;
    return { liveSlate: active, nextSlate: next, liveEntry: entry };
  }, [slates, stables]);

  const activeEntries = stables.filter(e =>
    e.status === 'submitted' || e.status === 'open'
  );
  const gamesPlayed   = allTimeTotals.gamesPlayed;
  const totalEarnings = allTimeTotals.totalEarnings;
  const virtualBank = userDoc?.remainingBankroll ?? 0;

  const featuredSlate = liveSlate || nextSlate;
  const closeMs       = toMs(featuredSlate?.gameCloseTime);
  const openMs        = toMs(featuredSlate?.gameOpenTime);
  const statusCode    = computeSlateStatus(featuredSlate);
  const closeCountdown = useCountdown(
    statusCode === 'live' ? closeMs : statusCode === 'opening_soon' ? openMs : null
  );

  const completedRaces = liveSlate?.completedRaces ?? 0;
  const totalRaces     = liveSlate?.totalRaces ?? 0;
  const progressPct    = totalRaces > 0 ? (completedRaces / totalRaces) * 100 : 0;

  const isEnterable = featuredSlate?.pricesLocked === true &&
    featuredSlate?.finalised !== true &&
    featuredSlate?.status !== 'finalised';

  const stableWidgetEntries = stables.slice(0, 3);

  // ── Dismiss notification ───────────────────────────────────────────────────
  async function dismissNotification(entryId, alertId) {
    const entry = stables.find(e => e.id === entryId);
    if (!entry) return;
    const updated = (entry.scratchingAlerts || []).map(a =>
      a.id === alertId ? { ...a, dismissed: true } : a
    );
    await updateDoc(doc(db, 'player_entries', entryId), {
      scratchingAlerts: updated,
    });
  }

  // ── Mark system notification as read ──────────────────────────────────────
  async function markNotifRead(notifId) {
    try {
      await updateDoc(
        doc(db, 'notifications', user.uid, 'items', notifId),
        { read: true }
      );
    } catch (e) {
      console.error('[Dashboard] markNotifRead error:', e.message);
    }
  }

  // ── Respond to friend request ──────────────────────────────────────────────
  async function respondToRequest(requestId, accept) {
    try {
      const fn = httpsCallable(
        functions,
        accept ? 'acceptFriendRequest' : 'declineFriendRequest'
      );
      await fn({ requestId });
    } catch (e) {
      console.error('[Dashboard] respondToRequest error:', e.code, e.message);
    }
  }

  // ── Derived notification count ─────────────────────────────────────────────
  const totalNotifications =
    systemNotifications.length + notifications.length + friendRequests.length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-container">
    <div className="dp-page">

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="dp-hero">
        <img src="/winning.png" alt="" className="dp-hero__img" />
        <div className="dp-hero__overlay" />
        <div className="dp-hero__content">
          <div className="dp-hero__left">
            <div className="dp-hero__profile">
              <img
                src={profileImageUrl}
                alt={firstName ? `${firstName}'s profile` : 'Profile'}
                className="dp-hero__profile-img"
                onError={e => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
              />
            </div>
            <h1 className="dp-greeting-name">{greetingText}</h1>
            <p className="dp-greeting-sub">Ready to play?</p>
            {userDoc?.stableSlug && (
              <Link to={`/stable/${userDoc.stableSlug}`} className="dp-greeting-profile-link">
                View Public Profile →
              </Link>
            )}
          </div>
          <div className="dp-hero__right">
            {liveSlate && (
              <p className="dp-hero__live-badge">
                🔥 A showdown is live — check your stable
              </p>
            )}
            <button
              className="dp-hero__action-btn"
              onClick={() => navigate('/showdowns')}
            >
              🐎 Browse Showdowns &nbsp;›
            </button>
          </div>
        </div>
      </div>

      {/* ── LIVE / UPCOMING SHOWDOWN CARD ────────────────────────────────── */}
      {featuredSlate && (
        <div className="dp-live-card">
          <div className="dp-live-card__left">
            <span className={`dp-live-badge${liveSlate ? '' : ' dp-live-badge--soon'}`}>
              {liveSlate ? '● LIVE' : '● UP NEXT'}
            </span>
            <span className="dp-live-card__section">
              {liveSlate ? 'LIVE SHOWDOWN' : 'UPCOMING SHOWDOWN'}
            </span>
            <h2 className="dp-live-card__name">
              {featuredSlate.gameName || featuredSlate.id}
            </h2>

            {liveSlate && (
              <>
                <div className="dp-live-card__progress-track">
                  <div
                    className="dp-live-card__progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <p className="dp-live-card__progress-label">
                  {completedRaces}/{totalRaces} races settled
                </p>
              </>
            )}

            <div className="dp-live-card__stats">
              {closeCountdown && (
                <div className="dp-live-stat">
                  <span className="dp-live-stat__label">
                    {liveSlate ? 'CLOSES IN' : 'OPENS IN'}
                  </span>
                  <span className="dp-live-stat__value dp-live-stat__value--amber">
                    {closeCountdown}
                  </span>
                </div>
              )}
              {liveEntry && liveEntry.remainingBankroll != null && (
                <div className="dp-live-stat">
                  <span className="dp-live-stat__label">BANKROLL LEFT</span>
                  <span className="dp-live-stat__value">
                    {fmt$(liveEntry.remainingBankroll)}
                  </span>
                </div>
              )}
              {liveEntry && (
                <div className="dp-live-stat">
                  <span className="dp-live-stat__label">VIRTUAL EARNINGS</span>
                  <span className="dp-live-stat__value dp-live-stat__value--green">
                    {fmt$(liveEntry.financialAudit?.totalPrizeDividendsWon ?? 0)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {isEnterable ? (
            <button
              className="dp-live-card__cta"
              onClick={() => navigate(`/slate/${featuredSlate.id}`)}
            >
              {liveEntry ? 'Continue →' : 'Enter Now →'}
            </button>
          ) : (
            <button className="dp-live-card__cta dp-live-card__cta--disabled" disabled>
              Opening Soon
            </button>
          )}
        </div>
      )}

      {/* ── INVITE FRIENDS ───────────────────────────────────────────────── */}
      {user && <ShareInviteCard user={user} userDoc={userDoc} />}

      {/* ── UPCOMING SCHEDULE (admin-planned meetings, not yet playable) ──── */}
      <UpcomingScheduleSection slates={slates} />

      {/* ── OVERVIEW STAT CARDS ──────────────────────────────────────────── */}
      <div className="dp-stat-grid">
        <div className="dp-stat-card">
          <span className="dp-stat-card__icon">🐎</span>
          <span className="dp-stat-card__label">Active Showdowns</span>
          <span className="dp-stat-card__value">{activeEntries.length}</span>
          <button className="dp-stat-card__link" onClick={() => navigate('/my-stable')}>
            View My Stable →
          </button>
        </div>
        <div className="dp-stat-card">
          <span className="dp-stat-card__icon">🏆</span>
          <span className="dp-stat-card__label">Games Played</span>
          <span className="dp-stat-card__value">{gamesPlayed}</span>
          <span className="dp-stat-card__sub">All Time</span>
        </div>
        <div className="dp-stat-card">
          <span className="dp-stat-card__icon">💰</span>
          <span className="dp-stat-card__label">Virtual Earnings</span>
          <span className="dp-stat-card__value">{fmt$(totalEarnings)}</span>
          <span className="dp-stat-card__sub">All Time</span>
        </div>
        <div className="dp-stat-card">
          <span className="dp-stat-card__icon">🏦</span>
          <span className="dp-stat-card__label">Virtual Bank</span>
          <span className="dp-stat-card__value">{fmt$(virtualBank)}</span>
        </div>
      </div>

      {/* ── MAIN TWO-COLUMN GRID ─────────────────────────────────────────── */}
      <div className="dp-grid">

        {/* LEFT COLUMN ─────────────────────────────────────────────────── */}
        <div className="dp-col-left">

          {/* My Stable widget */}
          <div className="dp-widget">
            <div className="dp-widget__header">
              <span className="dp-widget__title">MY STABLE</span>
              <button className="dp-widget__link" onClick={() => navigate('/my-stable')}>
                View Full Stable →
              </button>
            </div>
            {stableWidgetEntries.length === 0 ? (
              <p className="dp-widget__empty">
                No entries yet — browse showdowns to get started.
              </p>
            ) : stableWidgetEntries.map(entry => {
              const slate      = allSlates.find(s => s.id === entry.slateId);
              const isActive   = entry.status === 'submitted' && slate && !slate.finalised;
              const isUpcoming = slate && !slate.pricesLocked && !slate.finalised;
              const earnings   = entry.financialAudit?.totalPrizeDividendsWon ?? 0;
              const badgeKey   = isUpcoming ? 'upcoming' : isActive ? 'active' : 'completed';
              const badgeLabel = isUpcoming ? 'Upcoming' : isActive ? 'Active' : 'Completed';
              return (
                <div key={entry.id} className="dp-stable-row"
                  onClick={() => navigate('/my-stable')}>
                  <span className={`dp-stable-row__badge dp-stable-row__badge--${badgeKey}`}>
                    {badgeLabel}
                  </span>
                  <div className="dp-stable-row__info">
                    <span className="dp-stable-row__date">
                      {entry.slateId?.split('_')[0] ?? ''}
                    </span>
                    <span className="dp-stable-row__name">
                      {slate?.gameName ?? humaniseSlateId(entry.slateId)}
                    </span>
                  </div>
                  <div className="dp-stable-row__right">
                    {earnings > 0 && (
                      <span className="dp-stable-row__earnings">{fmt$(earnings)}</span>
                    )}
                    <span className="dp-stable-row__chevron">›</span>
                  </div>
                </div>
              );
            })}
            <button className="dp-widget__footer-link" onClick={() => navigate('/showdowns')}>
              Browse All Showdowns →
            </button>
          </div>

          {/* Recent Activity */}
          <div className="dp-widget">
            <div className="dp-widget__header">
              <span className="dp-widget__title">RECENT ACTIVITY</span>
              <button className="dp-widget__link">View All →</button>
            </div>
            {PLACEHOLDER_ACTIVITY.map(a => (
              <div key={a.id} className="dp-activity-row">
                <span className="dp-activity-row__icon">{a.icon}</span>
                <span className="dp-activity-row__text">{a.text}</span>
                <span className="dp-activity-row__time">{a.time}</span>
              </div>
            ))}
          </div>

          {/* Notifications widget — always visible */}
          <div className={`dp-widget${totalNotifications > 0 ? ' dp-widget--alert' : ''}`}>
            <div className="dp-widget__header">
              <span className="dp-widget__title">
                NOTIFICATIONS
                {totalNotifications > 0 && (
                  <span className="dp-notif-badge">{totalNotifications}</span>
                )}
              </span>
              {systemNotifications.length > 0 && (
                <button
                  type="button"
                  className="dp-widget__link dp-notif-dismiss-all"
                  onClick={dismissAllSystemNotifs}
                >
                  Dismiss All
                </button>
              )}
            </div>

            {totalNotifications === 0 && (
              <p className="dp-widget__empty">✓ All caught up — no notifications</p>
            )}

            {/* System notifications (broadcasts + welcome) — rendered first */}
            {systemNotifications.map(notif => (
              <div
                key={notif.id}
                className={`dp-notif-row dp-notif-row--${notif.type}`}
                style={{ cursor: 'pointer' }}
                onClick={() => markNotifRead(notif.id)}
              >
                <span className="dp-notif-row__icon">
                  {notif.type === 'welcome' ? '🎉' : '📢'}
                </span>
                <div className="dp-notif-row__body">
                  <span className="dp-notif-row__title">{notif.title}</span>
                  <span className="dp-notif-row__text">{notif.body}</span>
                </div>
                <div className="dp-notif-row__actions">
                  {notif.ctaUrl && (
                    <button
                      className="dp-notif-resolve"
                      onClick={e => {
                        e.stopPropagation();
                        navigate(notif.ctaUrl);
                        markNotifRead(notif.id);
                      }}
                    >
                      {notif.ctaLabel || 'View →'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="dp-notif-dismiss"
                    aria-label="Dismiss notification"
                    onClick={e => {
                      e.stopPropagation();
                      dismissSystemNotif(notif.id);
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}

            {/* Friend requests */}
            {friendRequests.map(req => (
              <div key={req.id} className="dp-notif-row dp-notif-row--friend">
                <span className="dp-notif-row__icon">👥</span>
                <div className="dp-notif-row__body">
                  <span className="dp-notif-row__text">
                    <strong>@{req.fromUsername}</strong> wants to connect with you on Stable Talk
                  </span>
                  {req.message && (
                    <span className="dp-notif-row__submsg">"{req.message}"</span>
                  )}
                </div>
                <div className="dp-notif-row__actions">
                  <button
                    className="dp-notif-accept"
                    onClick={() => respondToRequest(req.id, true)}
                  >
                    Accept
                  </button>
                  <button
                    className="dp-notif-decline"
                    onClick={() => respondToRequest(req.id, false)}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}

            {/* Scratching alerts below */}
            {notifications.map(alert => (
              <div key={alert.id} className="dp-notif-row dp-notif-row--alert">
                <span className="dp-notif-row__icon">⚠️</span>
                <div className="dp-notif-row__body">
                  <span className="dp-notif-row__text">
                    {alert.scratchedRunner} scratched — action required
                  </span>
                </div>
                <div className="dp-notif-row__actions">
                  {alert.allocationStatus === 'pending_choice' && (
                    <button
                      className="dp-notif-resolve"
                      onClick={() => navigate(
                        `/slate/${alert.slateId}?race=${alert.raceId}`
                      )}
                    >
                      Resolve →
                    </button>
                  )}
                  <button
                    className="dp-notif-dismiss"
                    onClick={() => dismissNotification(alert.entryId, alert.id)}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT COLUMN ────────────────────────────────────────────────── */}
        <div className="dp-col-right">

          {/* Recent Achievements (Phase 8) */}
          <div className="dp-widget">
            <div className="dp-widget__header">
              <span className="dp-widget__title">RECENT ACHIEVEMENTS</span>
              {userDoc?.stableSlug && (
                <button className="dp-widget__link" onClick={() => navigate(`/stable/${userDoc.stableSlug}`)}>
                  View All →
                </button>
              )}
            </div>
            {recentBadges.length === 0 ? (
              <p className="dp-widget__empty">Enter a Showdown to start earning trophies.</p>
            ) : recentBadges.map(badge => (
              <div key={badge.achievementKey} className="dp-award-row">
                <span className="dp-award-row__icon">{badge.icon}</span>
                <div className="dp-award-row__info">
                  <span className="dp-award-row__title">{badge.title}</span>
                  <span className={`dp-award-row__desc dp-award-row__desc--${badge.tier}`}>
                    {badge.tier?.charAt(0).toUpperCase() + badge.tier?.slice(1)}
                  </span>
                </div>
                <span className="dp-award-row__date">{formatRelativeTime(badge.earnedAt)}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="dp-widget">
            <div className="dp-widget__header">
              <span className="dp-widget__title">STATS</span>
              <button className="dp-widget__link">View Full Stats →</button>
            </div>
            <div className="dp-stats-grid">
              <div className="dp-stats-cell">
                <span className="dp-stats-cell__value">{gamesPlayed}</span>
                <span className="dp-stats-cell__label">Games Played</span>
              </div>
              <div className="dp-stats-cell">
                <span className="dp-stats-cell__value">—</span>
                <span className="dp-stats-cell__label">Top 10 Finishes</span>
              </div>
              <div className="dp-stats-cell">
                <span className="dp-stats-cell__value">—</span>
                <span className="dp-stats-cell__label">Games Won</span>
              </div>
              <div className="dp-stats-cell">
                <span className="dp-stats-cell__value">—</span>
                <span className="dp-stats-cell__label">Top 10 Rate</span>
              </div>
              <div className="dp-stats-cell">
                <span className="dp-stats-cell__value">{fmt$(totalEarnings)}</span>
                <span className="dp-stats-cell__label">Total Earnings</span>
              </div>
              <div className="dp-stats-cell">
                <span className="dp-stats-cell__value">—</span>
                <span className="dp-stats-cell__label">Best Single Game</span>
              </div>
            </div>
            {/* User Points / XP progress bar */}
            <div className="dp-xp-bar">
              <span className="dp-xp-bar__label">Level 1 — Rookie</span>
              <div className="dp-xp-bar__track">
                <div className="dp-xp-bar__fill" style={{ width: '10%' }} />
              </div>
              <span className="dp-xp-bar__sub">0 / 500 User Points</span>
            </div>
          </div>

          {/* Market Movers — placeholder */}
          <div className="dp-widget">
            <div className="dp-widget__header">
              <span className="dp-widget__title">MARKET MOVERS</span>
              <button className="dp-widget__link" onClick={() => navigate('/market')}>
                View Market →
              </button>
            </div>
            {PLACEHOLDER_MARKET.map(m => (
              <div key={m.id} className="dp-market-row">
                <span className="dp-market-row__icon">{m.hot ? '🔥' : '⚡'}</span>
                <span className="dp-market-row__name">{m.name}</span>
                <span className="dp-market-row__change">{m.change}</span>
                <span className="dp-market-row__sale">{m.sale}</span>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
    </div>
  );
}

export default DashboardPage;
